import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useAnimations';
import { useSeo } from '@/hooks/useSeo';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoopVideo } from '@/components/LoopVideo';

const HERO_IMG = '/about-hero.jpg';

const values = [
  {
    num: '01',
    title: 'Artigianalità',
    desc:
      'Ogni creazione è realizzata a mano dai nostri maestri orafi, seguendo tecniche centenarie perfezionate nel tempo.',
    img: 'https://images.unsplash.com/photo-1624588057318-5f1b2eb81012?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  },
  {
    num: '02',
    title: 'Qualità',
    desc:
      "Utilizziamo solo materiali pregiati e pietre certificate, garantendo l'eccellenza in ogni dettaglio.",
    img: 'https://images.unsplash.com/photo-1778077128762-594e292467a2?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  },
  {
    num: '03',
    title: 'Tradizione',
    desc:
      "Portiamo avanti l'eredità orafa napoletana, reinterpretandola con sensibilità contemporanea.",
    img: 'https://images.unsplash.com/photo-1777107509014-e3eaea7981bc?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  },
];

const stats = [
  { number: '3', label: 'Generazioni' },
  { number: '50+', label: 'Anni di Maestria' },
  { number: '10.000+', label: 'Clienti Soddisfatti' },
  { number: '∞', label: 'Passione' },
];

export default function AboutPage() {
  useSeo({
    title: 'Chi Siamo — Tre Generazioni di Maestria Orafa',
    description:
      "Dal 1968 la famiglia Gallotti crea gioielli artigianali nel cuore di Napoli. Scopri la nostra storia, i nostri valori e l'eredità che portiamo avanti.",
    path: '/chi-siamo',
  });

  const heroSection = useScrollAnimation(0.15);
  const storySection = useScrollAnimation(0.2);
  const statsSection = useScrollAnimation(0.2);
  const valuesSection = useScrollAnimation(0.15);

  return (
    <div className="bg-ivory" data-testid="about-page">
      {/* ============ HERO — Cinematic & Typographic ============ */}
      <section
        className="relative min-h-screen overflow-hidden flex items-center bg-ivory"
        data-testid="about-hero"
      >
        <span className="hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-center text-eyebrow text-muted whitespace-nowrap">
          Maison Gallotti — Napoli · MCMLXVIII
        </span>

        <div className="w-full max-w-container mx-auto px-6 md:px-12 lg:px-20 pt-32 pb-16 lg:py-24">
          <div
            ref={heroSection.ref}
            className={`grid grid-cols-12 items-center gap-y-10 scroll-fade-up ${heroSection.isVisible ? 'visible' : ''}`}
          >
            <div className="col-span-12 lg:col-span-7 relative z-10">
              <div className="flex items-center gap-4 mb-6 lg:mb-10">
                <div className="w-12 h-px bg-bordeaux" />
                <p className="text-eyebrow text-bordeaux">La Nostra Storia</p>
              </div>
              <h1
                data-testid="about-hero-title"
                className="font-display text-ink leading-[0.82] tracking-tighter"
              >
                <span className="block text-[24vw] lg:text-[12vw]">Chi</span>
                <span className="block text-[24vw] lg:text-[12vw] italic text-muted lg:pl-[16vw]">
                  Siamo
                </span>
              </h1>
            </div>

            <div className="col-span-12 lg:col-span-5 relative z-0 lg:-mt-32">
              <div className="image-shine aspect-[3/4] max-w-sm md:max-w-md mx-auto lg:ml-auto overflow-hidden rounded-2xl shadow-xl">
                <img
                  data-testid="about-hero-image"
                  src={HERO_IMG}
                  alt="Modella che indossa una creazione di alta gioielleria Ciro Gallotti"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STATS — Editorial Data ============ */}
      <section
        className="bg-ivory py-20 md:py-28 border-y border-line"
        data-testid="about-stats"
      >
        <div
          ref={statsSection.ref}
          className={`max-w-container mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-y-14 items-end scroll-fade-up ${statsSection.isVisible ? 'visible' : ''}`}
        >
          <div className="col-span-12 md:col-span-6">
            <p className="text-eyebrow text-muted mb-2">Anno di Fondazione</p>
            <p
              data-testid="about-stat-year"
              className="font-display italic text-bordeaux leading-[0.8] tracking-tighter text-[30vw] md:text-[13vw]"
            >
              1968
            </p>
          </div>

          <div className="col-span-12 md:col-span-6 md:pl-12 grid grid-cols-2 gap-x-8 gap-y-12">
            {stats.map((stat) => (
              <div key={stat.label} data-testid={`about-stat-${stat.label}`}>
                <p className="font-display text-ink leading-none text-5xl md:text-6xl tracking-tight">
                  {stat.number}
                </p>
                <p className="text-eyebrow text-muted mt-3">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SHOWROOM STORY — Asymmetric Splendor ============ */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-20"
        data-testid="about-story"
      >
        <div className="max-w-container mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="col-span-1 md:col-span-5 relative">
            <div className="image-shine overflow-hidden rounded-2xl shadow-xl aspect-[3/4] md:aspect-auto md:h-[80vh]">
              <LoopVideo
                src="/about-loop.mp4"
                poster="/about-poster.jpg"
                className="w-full h-full object-cover"
                testid="about-story-video"
              />
            </div>
            <div
              data-testid="about-story-badge"
              className="absolute -bottom-6 right-3 md:-right-8 bg-bordeaux text-ivory px-7 py-6 rounded-2xl shadow-lg"
            >
              <p className="font-display text-4xl md:text-5xl leading-none">50+</p>
              <p className="text-eyebrow text-ivory/80 mt-2">Anni di Storia</p>
            </div>
          </div>

          <div
            ref={storySection.ref}
            data-testid="about-story-text"
            className={`col-span-1 md:col-span-6 md:col-start-7 scroll-fade-up ${storySection.isVisible ? 'visible' : ''}`}
          >
            <p className="text-eyebrow text-bordeaux mb-4">Il Fondatore</p>
            <h2 className="font-display text-h1 text-ink mb-8 leading-[0.95] tracking-tight">
              La storia di
              <br />
              <span className="italic">Ciro Gallotti</span>
            </h2>
            <div className="space-y-6 text-ink/75 leading-relaxed max-w-reading font-light">
              <p className="text-lg">
                Per Ciro Gallotti l'oreficeria non è soltanto un mestiere.
                <span className="block font-display italic text-bordeaux text-2xl md:text-3xl mt-3">
                  È una vocazione.
                </span>
              </p>
              <p>
                La sua storia inizia simbolicamente quando aveva appena tre anni. Da bambino
                osservava con meraviglia il lavoro dell'orafo, affascinato dalla luce dell'oro, dal
                fuoco della fusione e dalla magia che trasformava un semplice metallo in un
                capolavoro.
              </p>
              <p>
                Quella curiosità infantile è diventata negli anni una passione autentica, coltivata
                con dedizione, studio e migliaia di ore trascorse al banco orafo.
              </p>
              <p>
                Oggi ogni gioiello firmato Ciro Gallotti porta con sé quella stessa emozione: il
                desiderio di creare qualcosa che possa attraversare il tempo e diventare parte della
                storia di chi lo indossa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CRAFT — Eccellenza Artigianale ============ */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-secondary/40 border-y border-line"
        data-testid="about-craft"
      >
        <div className="max-w-container mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="col-span-1 md:col-span-5 relative">
            <div className="image-shine overflow-hidden rounded-2xl shadow-xl aspect-[3/4]">
              <img
                src="/about-ciro.jpg"
                alt="Ciro Gallotti, fondatore e maestro orafo"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="about-ciro-image"
              />
            </div>
            <div className="absolute -bottom-6 -right-3 md:-right-8 bg-ink text-ivory px-7 py-6 rounded-2xl shadow-lg">
              <p className="font-display text-2xl md:text-3xl leading-none italic">Ciro Gallotti</p>
              <p className="text-eyebrow text-ivory/70 mt-2">Fondatore &amp; Maestro Orafo</p>
            </div>
          </div>

          <div className="col-span-1 md:col-span-6 md:col-start-7">
            <p className="text-eyebrow text-bordeaux mb-5">Made in Italy</p>
            <h2 className="font-display text-h1 text-ink leading-[1.02] tracking-tight mb-8">
              L'eccellenza dell'artigianato <span className="italic">italiano</span>
            </h2>
            <p className="font-display italic text-bordeaux text-2xl md:text-3xl mb-8">
              Ogni gioiello racconta una storia.
            </p>
            <div className="space-y-6 text-ink/75 leading-relaxed font-light max-w-reading">
              <p>
                Non è soltanto un oggetto prezioso, ma un simbolo destinato ad accompagnare i momenti
                più importanti della vita: un amore, una promessa, una conquista, un ricordo destinato
                a diventare eterno.
              </p>
              <p>
                Ciro Gallotti Jewelry nasce dall'incontro tra tradizione, passione e ricerca della
                perfezione. Ogni creazione prende vita interamente a mano, attraverso tecniche
                dell'alta oreficeria italiana tramandate nel tempo e reinterpretate con uno stile
                contemporaneo.
              </p>
              <p>
                Ogni dettaglio viene curato con precisione assoluta, perché il vero lusso non risiede
                soltanto nei materiali preziosi, ma nella qualità del lavoro artigianale e
                nell'unicità di ogni creazione.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ============ VALUES — The Kinetic List ============ */}
      <section
        className="py-24 md:py-32 bg-ivory"
        data-testid="about-values"
      >
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20">
          <div
            ref={valuesSection.ref}
            className={`max-w-2xl mb-12 md:mb-16 scroll-fade-up ${valuesSection.isVisible ? 'visible' : ''}`}
          >
            <p className="text-eyebrow text-bordeaux mb-4">I Nostri Valori</p>
            <h2 className="font-display text-h1 text-ink leading-tight tracking-tight">
              Cosa Ci <span className="italic">Guida</span>
            </h2>
          </div>
        </div>

        <ul
          data-testid="about-values-list"
          className="border-t border-line"
        >
          {values.map((item, i) => (
            <li
              key={item.num}
              data-testid={`about-value-item-${i + 1}`}
              className="group relative border-b border-line transition-colors duration-300 hover:bg-ink"
            >
              <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-9 md:py-12 flex items-center gap-5 md:gap-12">
                <span className="font-display text-xl md:text-2xl text-ink/30 group-hover:text-bordeaux transition-colors duration-300 w-10 md:w-14 flex-shrink-0">
                  {item.num}
                </span>
                <h3 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight text-ink group-hover:text-ivory group-hover:translate-x-2 md:group-hover:translate-x-4 transition-all duration-300">
                  <span className="group-hover:italic">{item.title}</span>
                </h3>
                <p className="hidden lg:block ml-auto max-w-[18rem] text-ink/55 group-hover:text-ivory/60 text-sm leading-relaxed transition-colors duration-300">
                  {item.desc}
                </p>
                <ArrowRight className="hidden md:block w-6 h-6 text-ink/0 group-hover:text-bordeaux -translate-x-3 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0 ml-auto lg:ml-8" />
              </div>

              <div className="pointer-events-none absolute top-1/2 right-[10%] -translate-y-1/2 w-52 h-64 rounded-2xl overflow-hidden opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500 hidden xl:block z-20 shadow-2xl rotate-3 group-hover:rotate-0">
                <img src={item.img} alt="" className="w-full h-full object-cover" />
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ============ CTA — Dramatic Contrast with Background Image ============ */}
      <section
        className="relative text-ivory py-28 md:py-40 px-6 md:px-12 lg:px-20 overflow-hidden"
        data-testid="about-cta-section"
      >
        <img
          src="/about-chisiamo-footer.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          data-testid="about-cta-bg"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/20" />
        <div className="relative z-10 max-w-container mx-auto text-center">
          <p className="text-eyebrow text-brass mb-8">Vieni in Showroom</p>
          <h2 className="font-display text-ivory leading-[0.85] tracking-tighter text-[15vw] md:text-[8vw]">
            Vieni a <span className="italic">Trovarci</span>
          </h2>
          <p className="text-ivory/85 text-lg max-w-xl mx-auto mt-10 mb-12 leading-relaxed font-light [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
            Ti invitiamo a varcare la soglia del nostro showroom nel cuore di Napoli, dove ogni
            creazione trova la sua luce e ogni storia diventa gioiello.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/appuntamento" data-testid="about-cta-book-btn">
              <Button size="xl" variant="accent" className="w-full sm:w-auto">
                Prenota un Appuntamento
              </Button>
            </Link>
            <Link to="/collezione" data-testid="about-cta-explore-btn">
              <Button
                size="xl"
                variant="outline"
                className="w-full sm:w-auto border-ivory/40 text-ivory bg-transparent hover:bg-ivory hover:text-ink"
              >
                Esplora la Collezione
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
